import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { MdClose, MdCheckCircle, MdError, MdWarning, MdInfo } from 'react-icons/md';
import { CustomButton } from './CustomButton';

interface Toast {
  id: string;
  title: string;
  description?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

interface ToastContextType {
  showSuccess: (title: string, description?: string) => void;
  showError: (title: string, description?: string) => void;
  showWarning: (title: string, description?: string) => void;
  showInfo: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

const getToastIcon = (type: Toast['type']) => {
  switch (type) {
    case 'success':
      return <MdCheckCircle size="20px" color="#48BB78" />;
    case 'error':
      return <MdError size="20px" color="#F56565" />;
    case 'warning':
      return <MdWarning size="20px" color="#ED8936" />;
    case 'info':
      return <MdInfo size="20px" color="#4299E1" />;
  }
};

const getToastBorderColor = (type: Toast['type']) => {
  switch (type) {
    case 'success':
      return '#48BB78';
    case 'error':
      return '#F56565';
    case 'warning':
      return '#ED8936';
    case 'info':
      return '#4299E1';
  }
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { ...toast, id };
    setToasts(prev => [...prev, newToast]);

    // Auto remove toast after duration
    setTimeout(() => {
      removeToast(id);
    }, toast.duration || 4000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showSuccess = (title: string, description?: string) => {
    addToast({ title, description, type: 'success', duration: 4000 });
  };

  const showError = (title: string, description?: string) => {
    addToast({ title, description, type: 'error', duration: 5000 });
  };

  const showWarning = (title: string, description?: string) => {
    addToast({ title, description, type: 'warning', duration: 4000 });
  };

  const showInfo = (title: string, description?: string) => {
    addToast({ title, description, type: 'info', duration: 3000 });
  };

  const value: ToastContextType = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast Container */}
      <Box
        position="fixed"
        top="20px"
        right="20px"
        zIndex={99999}
        display="flex"
        flexDirection="column"
        gap="12px"
        pointerEvents="none"
      >
        {toasts.map((toast) => (
          <Box
            key={toast.id}
            background="white"
            borderRadius="8px"
            boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
            padding="16px"
            border="1px solid #e2e8f0"
            borderLeft={`4px solid ${getToastBorderColor(toast.type)}`}
            maxWidth="400px"
            minWidth="300px"
            animation="slideIn 0.3s ease-out"
            pointerEvents="auto"
          >
            <Flex align="flex-start" gap="12px">
              {getToastIcon(toast.type)}
              <Box flex="1">
                <Text
                  fontSize="16px"
                  fontWeight="600"
                  color="#33324D"
                  marginBottom="4px"
                >
                  {toast.title}
                </Text>
                {toast.description && (
                  <Text
                    fontSize="14px"
                    color="#8B8B8B"
                    lineHeight="1.4"
                  >
                    {toast.description}
                  </Text>
                )}
              </Box>
              <CustomButton
                aria-label="Close toast"
                size="sm"
                visual="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeToast(toast.id);
                }}
                color="#8B8B8B"
                _hover={{ background: 'rgba(0, 0, 0, 0.05)' }}
                position="relative"
                zIndex={1}
              >
                <MdClose size="16px" />
              </CustomButton>
            </Flex>
          </Box>
        ))}
      </Box>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </ToastContext.Provider>
  );
}; 